import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";

export const usePersonPhotoUrl = (estateId: number | null, invitationToken?: string | null) => {
  const queryClient = useQueryClient();

  const {
    data: photoUrl,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["person-photo-url", estateId, invitationToken],
    queryFn: async () => {
      if (!estateId) return null;

      const url = new URL(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/media/profile-picture/url`,
      );
      url.searchParams.set("estate_id", String(estateId));

      if (invitationToken) {
        url.searchParams.set("invitation_token", invitationToken);
      }

      const headers: Record<string, string> = {
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      };

      if (!invitationToken) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        if (!accessToken) {
          throw new Error("No access token available");
        }
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        if (response.status === 404 || response.status === 403) {
          return null;
        }
        throw new Error("Failed to fetch photo URL");
      }

      const responseData = await response.json();

      if (responseData.error) {
        throw new Error(responseData.error);
      }

      const presignedUrl = responseData.photoUrl;
      if (!presignedUrl) return null;

      // Fetch the image and create a blob URL so it doesn't expire with the presigned URL
      const imageResponse = await fetch(presignedUrl);
      if (!imageResponse.ok) return null;

      const blob = await imageResponse.blob();
      return URL.createObjectURL(blob);
    },
    enabled: !!estateId,
    placeholderData: keepPreviousData,
    staleTime: 30 * 60 * 1000, // 30 minutes — blob URLs don't expire
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  const refreshPhotoUrl = () => {
    queryClient.invalidateQueries({
      queryKey: ["person-photo-url", estateId, invitationToken],
    });
  };

  return {
    photoUrl: photoUrl ?? null,
    isLoading,
    error: error?.message || null,
    refreshPhotoUrl,
  };
};
